import JSZip from 'jszip'
import type { AnimationTrigger, AnimationType, PPTAnimation, Slide } from '@/types/slides'

type ExportAnimation = Pick<PPTAnimation, 'elId' | 'effect' | 'type' | 'duration' | 'trigger'>

type FlyDirection = 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'

interface ResolvedAnimation extends ExportAnimation {
  shapeId: number
}

const FADE_IN_EFFECTS = new Set(['fadeIn'])
const FADE_OUT_EFFECTS = new Set(['fadeOut'])
const FLOAT_IN_DOWN_EFFECTS = new Set(['fadeInDown', 'fadeInDownBig'])
const FLOAT_IN_UP_EFFECTS = new Set(['fadeInUp', 'fadeInUpBig'])
const ZOOM_IN_EFFECTS = new Set(['zoomIn'])
const ZOOM_OUT_EFFECTS = new Set(['zoomOut'])
const ZOOM_IN_DIRECTION: Record<string, FlyDirection> = {
  zoomInDown: 'bottom',
  zoomInUp: 'top',
  zoomInLeft: 'left',
  zoomInRight: 'right',
}
const ZOOM_OUT_DIRECTION: Record<string, FlyDirection> = {
  zoomOutDown: 'bottom',
  zoomOutUp: 'top',
  zoomOutLeft: 'left',
  zoomOutRight: 'right',
}
const BOUNCE_IN_EFFECTS = new Set(['bounceIn', 'bounceInDown', 'bounceInUp', 'bounceInLeft', 'bounceInRight'])
const BOUNCE_OUT_EFFECTS = new Set(['bounceOut', 'bounceOutDown', 'bounceOutUp', 'bounceOutLeft', 'bounceOutRight'])
const BACK_IN_EFFECTS = new Set(['backInDown', 'backInUp', 'backInLeft', 'backInRight'])
const BACK_OUT_EFFECTS = new Set(['backOutDown', 'backOutUp', 'backOutLeft', 'backOutRight'])

const BOUNCE_IN_AXIS: Record<string, 'ppt_x' | 'ppt_y'> = {
  bounceIn: 'ppt_y',
  bounceInDown: 'ppt_y',
  bounceInUp: 'ppt_y',
  bounceInLeft: 'ppt_x',
  bounceInRight: 'ppt_x',
}

const BACK_IN_DIRECTION: Record<string, FlyDirection> = {
  backInDown: 'top',
  backInUp: 'bottom',
  backInLeft: 'right',
  backInRight: 'left',
}

const BACK_OUT_DIRECTION: Record<string, FlyDirection> = {
  backOutDown: 'bottom',
  backOutUp: 'top',
  backOutLeft: 'left',
  backOutRight: 'right',
}

const SHAKE_X_EFFECTS = new Set(['shakeX', 'headShake'])
const SHAKE_Y_EFFECTS = new Set(['shakeY'])
const TEETER_EFFECTS = new Set(['swing', 'wobble', 'tada', 'jello'])
const PULSE_EFFECTS = new Set(['pulse', 'heartBeat', 'bounce'])
const FLASH_EFFECTS = new Set(['flash'])
const GROW_SHRINK_EFFECTS = new Set(['rubberBand'])

const FLY_IN_DIRECTION: Record<string, FlyDirection> = {
  fadeInLeft: 'left',
  fadeInLeftBig: 'left',
  fadeInRight: 'right',
  fadeInRightBig: 'right',
  fadeInTopLeft: 'topLeft',
  fadeInTopRight: 'topRight',
  fadeInBottomLeft: 'bottomLeft',
  fadeInBottomRight: 'bottomRight',
  slideInDown: 'top',
  slideInUp: 'bottom',
  slideInLeft: 'left',
  slideInRight: 'right',
  lightSpeedInRight: 'right',
  lightSpeedInLeft: 'left',
  rotateIn: 'bottom',
  rotateInDownLeft: 'bottomLeft',
  rotateInDownRight: 'bottomRight',
  rotateInUpLeft: 'topLeft',
  rotateInUpRight: 'topRight',
  flipInX: 'bottom',
  flipInY: 'left',
}

const FLY_OUT_DIRECTION: Record<string, FlyDirection> = {
  fadeOutLeft: 'left',
  fadeOutLeftBig: 'left',
  fadeOutRight: 'right',
  fadeOutRightBig: 'right',
  fadeOutTopLeft: 'topLeft',
  fadeOutTopRight: 'topRight',
  fadeOutBottomLeft: 'bottomLeft',
  fadeOutBottomRight: 'bottomRight',
  slideOutDown: 'bottom',
  slideOutUp: 'top',
  slideOutLeft: 'left',
  slideOutRight: 'right',
  lightSpeedOutRight: 'right',
  lightSpeedOutLeft: 'left',
  rotateOut: 'bottom',
  rotateOutDownLeft: 'bottomLeft',
  rotateOutDownRight: 'bottomRight',
  rotateOutUpLeft: 'topLeft',
  rotateOutUpRight: 'topRight',
  flipOutX: 'top',
  flipOutY: 'left',
  fadeOutDown: 'bottom',
  fadeOutDownBig: 'bottom',
  fadeOutUp: 'top',
  fadeOutUpBig: 'top',
}

const FLY_COORDS: Record<FlyDirection, { startX: string; endX: string; startY: string; endY: string }> = {
  bottom: { startX: '#ppt_x', endX: '#ppt_x', startY: '1+#ppt_h/2', endY: '#ppt_y' },
  bottomLeft: { startX: '0-#ppt_w/2', endX: '#ppt_x', startY: '1+#ppt_h/2', endY: '#ppt_y' },
  left: { startX: '0-#ppt_w/2', endX: '#ppt_x', startY: '#ppt_y', endY: '#ppt_y' },
  topLeft: { startX: '0-#ppt_w/2', endX: '#ppt_x', startY: '0-#ppt_h/2', endY: '#ppt_y' },
  top: { startX: '#ppt_x', endX: '#ppt_x', startY: '0-#ppt_h/2', endY: '#ppt_y' },
  topRight: { startX: '1+#ppt_w/2', endX: '#ppt_x', startY: '0-#ppt_h/2', endY: '#ppt_y' },
  right: { startX: '1+#ppt_w/2', endX: '#ppt_x', startY: '#ppt_y', endY: '#ppt_y' },
  bottomRight: { startX: '1+#ppt_w/2', endX: '#ppt_x', startY: '1+#ppt_h/2', endY: '#ppt_y' },
}

function resolveNodeType(trigger: AnimationTrigger): string {
  if (trigger === 'meantime') return 'withEffect'
  if (trigger === 'auto') return 'afterEffect'
  return 'clickEffect'
}

function visibilitySet(shapeId: number, nodeId: number, visible: boolean, delay = 0): string {
  const val = visible ? 'visible' : 'hidden'
  return [
    '<p:set>',
    '<p:cBhvr>',
    `<p:cTn id="${nodeId}" dur="1" fill="hold"><p:stCondLst><p:cond delay="${delay}"/></p:stCondLst></p:cTn>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    '<p:attrNameLst><p:attrName>style.visibility</p:attrName></p:attrNameLst>',
    '</p:cBhvr>',
    `<p:to><p:strVal val="${val}"/></p:to>`,
    '</p:set>',
  ].join('')
}

function animEffect(shapeId: number, nodeId: number, duration: number, transition: 'in' | 'out', filter: string): string {
  return [
    `<p:animEffect transition="${transition}" filter="${filter}">`,
    `<p:cBhvr><p:cTn id="${nodeId}" dur="${duration}"/><p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl></p:cBhvr>`,
    '</p:animEffect>',
  ].join('')
}

function axisAnim(shapeId: number, nodeId: number, duration: number, axis: 'ppt_x' | 'ppt_y', start: string, end: string, tmFilter?: string): string {
  const tmAttr = tmFilter ? ` tmFilter="${tmFilter}"` : ''
  return [
    '<p:anim calcmode="lin" valueType="num">',
    '<p:cBhvr additive="base">',
    `<p:cTn id="${nodeId}" dur="${duration}" fill="hold"${tmAttr}/>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    `<p:attrNameLst><p:attrName>${axis}</p:attrName></p:attrNameLst>`,
    '</p:cBhvr>',
    '<p:tavLst>',
    `<p:tav tm="0"><p:val><p:strVal val="${start}"/></p:val></p:tav>`,
    `<p:tav tm="100000"><p:val><p:strVal val="${end}"/></p:val></p:tav>`,
    '</p:tavLst>',
    '</p:anim>',
  ].join('')
}

function scaleAxisAnimBackIn(shapeId: number, nodeId: number, duration: number, axis: 'ppt_w' | 'ppt_h'): string {
  const full = axis === 'ppt_w' ? '#ppt_w' : '#ppt_h'
  const small = axis === 'ppt_w' ? '0.7*#ppt_w' : '0.7*#ppt_h'
  return [
    '<p:anim calcmode="lin" valueType="num">',
    '<p:cBhvr additive="base">',
    `<p:cTn id="${nodeId}" dur="${duration}" fill="hold" tmFilter="0,0;0.8,0.8;1,1"/>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    `<p:attrNameLst><p:attrName>${axis}</p:attrName></p:attrNameLst>`,
    '</p:cBhvr>',
    '<p:tavLst>',
    `<p:tav tm="0"><p:val><p:strVal val="${small}"/></p:val></p:tav>`,
    `<p:tav tm="80000"><p:val><p:strVal val="${small}"/></p:val></p:tav>`,
    `<p:tav tm="100000"><p:val><p:strVal val="${full}"/></p:val></p:tav>`,
    '</p:tavLst>',
    '</p:anim>',
  ].join('')
}

/** backIn* 专用飞入坐标：起始位置更远，贴近 animate.css 长距滑入 */
const BACK_IN_FLY_COORDS: Record<FlyDirection, { startX: string; endX: string; startY: string; endY: string }> = {
  bottom: { startX: '#ppt_x', endX: '#ppt_x', startY: 'ppt_y+0.85', endY: '#ppt_y' },
  top: { startX: '#ppt_x', endX: '#ppt_x', startY: 'ppt_y-0.85', endY: '#ppt_y' },
  left: { startX: '#ppt_x-0.85', endX: '#ppt_x', startY: '#ppt_y', endY: '#ppt_y' },
  right: { startX: 'ppt_x+0.85', endX: '#ppt_x', startY: '#ppt_y', endY: '#ppt_y' },
  topLeft: { startX: '#ppt_x-0.6', endX: '#ppt_x', startY: 'ppt_y-0.6', endY: '#ppt_y' },
  topRight: { startX: 'ppt_x+0.6', endX: '#ppt_x', startY: 'ppt_y-0.6', endY: '#ppt_y' },
  bottomLeft: { startX: '#ppt_x-0.6', endX: '#ppt_x', startY: 'ppt_y+0.6', endY: '#ppt_y' },
  bottomRight: { startX: 'ppt_x+0.6', endX: '#ppt_x', startY: 'ppt_y+0.6', endY: '#ppt_y' },
}

function scaleAxisAnim(shapeId: number, nodeId: number, duration: number, axis: 'ppt_w' | 'ppt_h', start: string, end: string): string {
  const valTag = (v: string) => (v.startsWith('#') || v.includes('ppt_')
    ? `<p:strVal val="${v}"/>`
    : `<p:fltVal val="${v}"/>`)
  return [
    '<p:anim calcmode="lin" valueType="num">',
    '<p:cBhvr additive="base">',
    `<p:cTn id="${nodeId}" dur="${duration}" fill="hold"/>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    `<p:attrNameLst><p:attrName>${axis}</p:attrName></p:attrNameLst>`,
    '</p:cBhvr>',
    '<p:tavLst>',
    `<p:tav tm="0"><p:val>${valTag(start)}</p:val></p:tav>`,
    `<p:tav tm="100000"><p:val>${valTag(end)}</p:val></p:tav>`,
    '</p:tavLst>',
    '</p:anim>',
  ].join('')
}

function oscillateAxisAnim(shapeId: number, nodeId: number, duration: number, axis: 'ppt_x' | 'ppt_y', amplitude: string): string {
  const base = axis === 'ppt_x' ? '#ppt_x' : '#ppt_y'
  const frames = [
    [0, base],
    [20000, `${base}${amplitude}`],
    [40000, `${base}-${amplitude}`],
    [60000, `${base}${amplitude}`],
    [80000, `${base}-${amplitude}`],
    [100000, base],
  ] as const
  const tavs = frames.map(([tm, val]) => `<p:tav tm="${tm}"><p:val><p:strVal val="${val}"/></p:val></p:tav>`).join('')
  return [
    '<p:anim calcmode="lin" valueType="num">',
    '<p:cBhvr additive="base">',
    `<p:cTn id="${nodeId}" dur="${duration}" fill="hold"/>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    `<p:attrNameLst><p:attrName>${axis}</p:attrName></p:attrNameLst>`,
    '</p:cBhvr>',
    `<p:tavLst>${tavs}</p:tavLst>`,
    '</p:anim>',
  ].join('')
}

function genFadeEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, transition: 'in' | 'out'): string {
  const preset = transition === 'in'
    ? { presetID: 10, presetClass: 'entr', presetSubtype: 0 }
    : { presetID: 10, presetClass: 'exit', presetSubtype: 0 }

  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="${preset.presetID}" presetClass="${preset.presetClass}" presetSubtype="${preset.presetSubtype}" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  if (transition === 'in') {
    xml += visibilitySet(shapeId, nodeId + 1, true)
    xml += animEffect(shapeId, nodeId + 2, duration, 'in', 'fade')
  }
  else {
    xml += animEffect(shapeId, nodeId + 1, duration, 'out', 'fade')
    xml += visibilitySet(shapeId, nodeId + 2, false, Math.max(0, duration - 1))
  }
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genFloatInEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, direction: 'up' | 'down'): string {
  const startY = direction === 'down' ? '#ppt_y-.1' : '#ppt_y+.1'
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="42" presetClass="entr" presetSubtype="8" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += visibilitySet(shapeId, nodeId + 1, true)
  xml += animEffect(shapeId, nodeId + 2, duration, 'in', 'fade')
  xml += axisAnim(shapeId, nodeId + 3, duration, 'ppt_x', '#ppt_x', '#ppt_x')
  xml += axisAnim(shapeId, nodeId + 4, duration, 'ppt_y', startY, '#ppt_y')
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genFlyEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, direction: FlyDirection, transition: 'in' | 'out'): string {
  const flySubtypeMap: Record<FlyDirection, number> = {
    top: 1, right: 2, topRight: 3, bottom: 4, left: 8, topLeft: 9, bottomLeft: 12, bottomRight: 6,
  }
  const presetClass = transition === 'in' ? 'entr' : 'exit'
  const coords = FLY_COORDS[direction]
  const startX = transition === 'in' ? coords.startX : coords.endX
  const endX = transition === 'in' ? coords.endX : coords.startX
  const startY = transition === 'in' ? coords.startY : coords.endY
  const endY = transition === 'in' ? coords.endY : coords.startY

  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="2" presetClass="${presetClass}" presetSubtype="${flySubtypeMap[direction]}" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  if (transition === 'in') xml += visibilitySet(shapeId, nodeId + 1, true)
  xml += axisAnim(shapeId, nodeId + 2, duration, 'ppt_x', startX, endX)
  xml += axisAnim(shapeId, nodeId + 3, duration, 'ppt_y', startY, endY)
  if (transition === 'out') xml += visibilitySet(shapeId, nodeId + 4, false, Math.max(0, duration - 1))
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genZoomEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, transition: 'in' | 'out'): string {
  const presetClass = transition === 'in' ? 'entr' : 'exit'
  const presetSubtype = transition === 'in' ? 16 : 32
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="53" presetClass="${presetClass}" presetSubtype="${presetSubtype}" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  if (transition === 'in') {
    xml += visibilitySet(shapeId, nodeId + 1, true)
    xml += scaleAxisAnim(shapeId, nodeId + 2, duration, 'ppt_w', '0', '#ppt_w')
    xml += scaleAxisAnim(shapeId, nodeId + 3, duration, 'ppt_h', '0', '#ppt_h')
    xml += animEffect(shapeId, nodeId + 4, duration, 'in', 'fade')
  }
  else {
    xml += scaleAxisAnim(shapeId, nodeId + 1, duration, 'ppt_w', '#ppt_w', '0')
    xml += scaleAxisAnim(shapeId, nodeId + 2, duration, 'ppt_h', '#ppt_h', '0')
    xml += animEffect(shapeId, nodeId + 3, duration, 'out', 'fade')
    xml += visibilitySet(shapeId, nodeId + 4, false, Math.max(0, duration - 1))
  }
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genAppearEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="1" presetClass="entr" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += visibilitySet(shapeId, nodeId + 1, true, duration)
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genDisappearEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="1" presetClass="exit" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += visibilitySet(shapeId, nodeId + 1, false, duration)
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

/** 弹入：WPS/PPT「弹跳」入场近似 */
function genBounceInEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, effect: string): string {
  const axis = BOUNCE_IN_AXIS[effect] || 'ppt_y'
  const isVertical = axis === 'ppt_y'
  let startMain = isVertical ? 'ppt_y-0.333' : '#ppt_x-0.25'
  if (effect === 'bounceInUp') startMain = 'ppt_y+0.333'
  if (effect === 'bounceInDown') startMain = 'ppt_y-0.333'
  if (effect === 'bounceInLeft') startMain = '#ppt_x-0.25'
  if (effect === 'bounceInRight') startMain = '#ppt_x+0.25'
  const endMain = isVertical ? '#ppt_y' : '#ppt_x'
  const bounceBack = isVertical ? 'ppt_y-0.05' : '#ppt_x-0.03'

  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="26" presetClass="entr" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += visibilitySet(shapeId, nodeId + 1, true)
  xml += animEffect(shapeId, nodeId + 2, duration, 'in', 'fade')
  if (isVertical) {
    xml += axisAnim(shapeId, nodeId + 3, Math.round(duration * 0.66), 'ppt_y', startMain, endMain, '0.0,0.0;0.25,0.07;0.50,0.2;0.75,0.467;1.0,1.0')
    xml += axisAnim(shapeId, nodeId + 4, Math.round(duration * 0.34), 'ppt_y', endMain, bounceBack, '0, 0; 0.125,0.2665; 0.25,0.4; 0.375,0.465; 0.5,0.5; 0.625,0.535; 0.75,0.6; 0.875,0.7335; 1,1')
    xml += axisAnim(shapeId, nodeId + 5, Math.round(duration * 0.34), 'ppt_y', bounceBack, endMain, '0, 0; 0.125,0.2665; 0.25,0.4; 0.375,0.465; 0.5,0.5; 0.625,0.535; 0.75,0.6; 0.875,0.7335; 1,1')
  }
  else {
    xml += axisAnim(shapeId, nodeId + 3, duration, 'ppt_x', startMain, endMain, '0,0; 0.14,0.36; 0.43,0.73; 0.71,0.91; 1.0,1.0')
    xml += axisAnim(shapeId, nodeId + 4, duration, 'ppt_y', '#ppt_y', '#ppt_y')
  }
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

/** 放大滑入：先长距滑入（保持 70% 大小），再放大到 100%，贴近 animate.css backIn* */
function genBackInEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, direction: FlyDirection): string {
  const coords = BACK_IN_FLY_COORDS[direction]
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="53" presetClass="entr" presetSubtype="16" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += visibilitySet(shapeId, nodeId + 1, true)
  xml += scaleAxisAnimBackIn(shapeId, nodeId + 2, duration, 'ppt_w')
  xml += scaleAxisAnimBackIn(shapeId, nodeId + 3, duration, 'ppt_h')
  xml += animEffect(shapeId, nodeId + 4, duration, 'in', 'fade')
  xml += axisAnim(shapeId, nodeId + 5, duration, 'ppt_x', coords.startX, coords.endX, '0,0;0.8,0.8;1,1')
  xml += axisAnim(shapeId, nodeId + 6, duration, 'ppt_y', coords.startY, coords.endY, '0,0;0.8,0.8;1,1')
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genBackOutEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, direction: FlyDirection): string {
  const coords = FLY_COORDS[direction]
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="53" presetClass="exit" presetSubtype="32" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += scaleAxisAnim(shapeId, nodeId + 1, duration, 'ppt_w', '#ppt_w', '0')
  xml += scaleAxisAnim(shapeId, nodeId + 2, duration, 'ppt_h', '#ppt_h', '0')
  xml += animEffect(shapeId, nodeId + 3, duration, 'out', 'fade')
  xml += axisAnim(shapeId, nodeId + 4, duration, 'ppt_x', coords.endX, coords.startX)
  xml += axisAnim(shapeId, nodeId + 5, duration, 'ppt_y', coords.endY, coords.startY)
  xml += visibilitySet(shapeId, nodeId + 6, false, Math.max(0, duration - 1))
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genShakeEffect(shapeId: number, nodeId: number, duration: number, nodeType: string, axis: 'ppt_x' | 'ppt_y'): string {
  const amplitude = axis === 'ppt_x' ? '+0.03' : '+0.04'
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="32" presetClass="emph" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += oscillateAxisAnim(shapeId, nodeId + 1, duration, axis, amplitude)
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genTeeterEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  const scale = duration / 1000
  const dur1 = Math.max(1, Math.round(100 * scale))
  const dur2 = Math.max(1, Math.round(200 * scale))
  const delay2 = Math.round(200 * scale)
  const delay3 = Math.round(400 * scale)
  const delay4 = Math.round(600 * scale)
  const delay5 = Math.round(800 * scale)

  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="32" presetClass="emph" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'

  const rot = (id: number, by: string, dur: number, delay: number) => [
    `<p:animRot by="${by}">`,
    `<p:cBhvr><p:cTn id="${id}" dur="${dur}" fill="hold"><p:stCondLst><p:cond delay="${delay}"/></p:stCondLst></p:cTn>`,
    `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl>`,
    '<p:attrNameLst><p:attrName>r</p:attrName></p:attrNameLst></p:cBhvr>',
    '</p:animRot>',
  ].join('')

  xml += rot(nodeId + 1, '120000', dur1, 0)
  xml += rot(nodeId + 2, '-240000', dur2, delay2)
  xml += rot(nodeId + 3, '240000', dur2, delay3)
  xml += rot(nodeId + 4, '-240000', dur2, delay4)
  xml += rot(nodeId + 5, '120000', dur2, delay5)
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genPulseEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="26" presetClass="emph" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += '<p:animScale>'
  xml += `<p:cBhvr><p:cTn id="${nodeId + 1}" dur="${Math.round(duration / 2)}" fill="hold"/>`
  xml += `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl></p:cBhvr>`
  xml += '<p:to x="110000" y="110000"/>'
  xml += '</p:animScale>'
  xml += '<p:animScale>'
  xml += `<p:cBhvr><p:cTn id="${nodeId + 2}" dur="${Math.round(duration / 2)}" fill="hold"><p:stCondLst><p:cond delay="${Math.round(duration / 2)}"/></p:stCondLst></p:cTn>`
  xml += `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl></p:cBhvr>`
  xml += '<p:to x="100000" y="100000"/>'
  xml += '</p:animScale>'
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genFlashEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="14" presetClass="emph" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += animEffect(shapeId, nodeId + 1, Math.round(duration / 3), 'in', 'fade')
  xml += animEffect(shapeId, nodeId + 2, Math.round(duration / 3), 'out', 'fade')
  xml += animEffect(shapeId, nodeId + 3, Math.round(duration / 3), 'in', 'fade')
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genGrowShrinkEffect(shapeId: number, nodeId: number, duration: number, nodeType: string): string {
  let xml = '<p:par>'
  xml += `<p:cTn id="${nodeId}" presetID="6" presetClass="emph" presetSubtype="0" fill="hold" grpId="0" nodeType="${nodeType}">`
  xml += '<p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>'
  xml += '<p:animScale>'
  xml += `<p:cBhvr><p:cTn id="${nodeId + 1}" dur="${Math.round(duration / 2)}" fill="hold"/>`
  xml += `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl></p:cBhvr>`
  xml += '<p:by x="125000" y="75000"/>'
  xml += '</p:animScale>'
  xml += '<p:animScale>'
  xml += `<p:cBhvr><p:cTn id="${nodeId + 2}" dur="${Math.round(duration / 2)}" fill="hold"><p:stCondLst><p:cond delay="${Math.round(duration / 2)}"/></p:stCondLst></p:cTn>`
  xml += `<p:tgtEl><p:spTgt spid="${shapeId}"/></p:tgtEl></p:cBhvr>`
  xml += '<p:by x="80000" y="133333"/>'
  xml += '</p:animScale>'
  xml += '</p:childTnLst></p:cTn></p:par>'
  return xml
}

function genEffectXml(anim: ResolvedAnimation, nodeId: number): string {
  const duration = Math.max(1, Math.round(anim.duration || 1000))
  const nodeType = resolveNodeType(anim.trigger)
  const { effect, type } = anim

  if (FADE_IN_EFFECTS.has(effect) || (type === 'in' && effect === 'fadeIn')) {
    return genFadeEffect(anim.shapeId, nodeId, duration, nodeType, 'in')
  }
  if (FADE_OUT_EFFECTS.has(effect)) {
    return genFadeEffect(anim.shapeId, nodeId, duration, nodeType, 'out')
  }
  if (FLOAT_IN_DOWN_EFFECTS.has(effect)) {
    return genFloatInEffect(anim.shapeId, nodeId, duration, nodeType, 'down')
  }
  if (FLOAT_IN_UP_EFFECTS.has(effect)) {
    return genFloatInEffect(anim.shapeId, nodeId, duration, nodeType, 'up')
  }
  if (ZOOM_IN_EFFECTS.has(effect)) {
    return genZoomEffect(anim.shapeId, nodeId, duration, nodeType, 'in')
  }
  if (ZOOM_IN_DIRECTION[effect]) {
    return genBackInEffect(anim.shapeId, nodeId, duration, nodeType, ZOOM_IN_DIRECTION[effect])
  }
  if (ZOOM_OUT_EFFECTS.has(effect)) {
    return genZoomEffect(anim.shapeId, nodeId, duration, nodeType, 'out')
  }
  if (ZOOM_OUT_DIRECTION[effect]) {
    return genBackOutEffect(anim.shapeId, nodeId, duration, nodeType, ZOOM_OUT_DIRECTION[effect])
  }
  if (BOUNCE_IN_EFFECTS.has(effect)) {
    return genBounceInEffect(anim.shapeId, nodeId, duration, nodeType, effect)
  }
  if (BOUNCE_OUT_EFFECTS.has(effect)) {
    const bounceOutDir: Record<string, FlyDirection> = {
      bounceOutDown: 'bottom',
      bounceOutUp: 'top',
      bounceOutLeft: 'left',
      bounceOutRight: 'right',
      bounceOut: 'bottom',
    }
    return genFlyEffect(anim.shapeId, nodeId, duration, nodeType, bounceOutDir[effect] || 'bottom', 'out')
  }
  if (BACK_IN_EFFECTS.has(effect)) {
    return genBackInEffect(anim.shapeId, nodeId, duration, nodeType, BACK_IN_DIRECTION[effect])
  }
  if (BACK_OUT_EFFECTS.has(effect)) {
    return genBackOutEffect(anim.shapeId, nodeId, duration, nodeType, BACK_OUT_DIRECTION[effect])
  }
  if (FLY_IN_DIRECTION[effect]) {
    return genFlyEffect(anim.shapeId, nodeId, duration, nodeType, FLY_IN_DIRECTION[effect], 'in')
  }
  if (FLY_OUT_DIRECTION[effect]) {
    return genFlyEffect(anim.shapeId, nodeId, duration, nodeType, FLY_OUT_DIRECTION[effect], 'out')
  }
  if (type === 'out') {
    return genFadeEffect(anim.shapeId, nodeId, duration, nodeType, 'out')
  }
  if (SHAKE_X_EFFECTS.has(effect)) {
    return genShakeEffect(anim.shapeId, nodeId, duration, nodeType, 'ppt_x')
  }
  if (SHAKE_Y_EFFECTS.has(effect)) {
    return genShakeEffect(anim.shapeId, nodeId, duration, nodeType, 'ppt_y')
  }
  if (TEETER_EFFECTS.has(effect)) {
    return genTeeterEffect(anim.shapeId, nodeId, duration, nodeType)
  }
  if (PULSE_EFFECTS.has(effect)) {
    return genPulseEffect(anim.shapeId, nodeId, duration, nodeType)
  }
  if (FLASH_EFFECTS.has(effect)) {
    return genFlashEffect(anim.shapeId, nodeId, duration, nodeType)
  }
  if (GROW_SHRINK_EFFECTS.has(effect)) {
    return genGrowShrinkEffect(anim.shapeId, nodeId, duration, nodeType)
  }
  if (type === 'attention') {
    return genTeeterEffect(anim.shapeId, nodeId, duration, nodeType)
  }
  return genFadeEffect(anim.shapeId, nodeId, duration, nodeType, 'in')
}

interface AnimationGroup {
  onClick: ResolvedAnimation[]
  withPrevious: ResolvedAnimation[]
  afterPrevious: ResolvedAnimation[]
  previousDuration: number
}

function groupAnimationsByTrigger(animations: ResolvedAnimation[]): AnimationGroup[] {
  const groups: AnimationGroup[] = []
  let currentGroup: AnimationGroup | null = null

  for (const anim of animations) {
    if (anim.trigger === 'click' || !currentGroup) {
      currentGroup = {
        onClick: [anim],
        withPrevious: [],
        afterPrevious: [],
        previousDuration: anim.duration || 1000,
      }
      groups.push(currentGroup)
      continue
    }
    if (anim.trigger === 'meantime') {
      currentGroup.withPrevious.push(anim)
      continue
    }
    currentGroup.afterPrevious.push(anim)
    const totalDuration = (anim.duration || 1000)
    currentGroup.previousDuration = Math.max(currentGroup.previousDuration, totalDuration)
  }

  return groups
}

function buildTimingXml(animations: ResolvedAnimation[]): string {
  const groups = groupAnimationsByTrigger(animations)
  let nodeId = 3
  let effectBlocks = ''
  let cumulativeDelay = 0

  for (const group of groups) {
    effectBlocks += '<p:par>'
    effectBlocks += `<p:cTn id="${nodeId}" fill="hold">`
    effectBlocks += '<p:stCondLst><p:cond delay="indefinite"/><p:cond evt="onBegin" delay="0"><p:tn val="2"/></p:cond></p:stCondLst>'
    effectBlocks += '<p:childTnLst>'
    nodeId++

    effectBlocks += '<p:par>'
    effectBlocks += `<p:cTn id="${nodeId}" fill="hold"><p:stCondLst><p:cond delay="0"/></p:stCondLst><p:childTnLst>`
    nodeId++

    for (const anim of group.onClick) {
      effectBlocks += genEffectXml(anim, nodeId)
      nodeId += 30
      cumulativeDelay = (anim.duration || 1000)
    }
    for (const anim of group.withPrevious) {
      effectBlocks += genEffectXml(anim, nodeId)
      nodeId += 30
    }

    effectBlocks += '</p:childTnLst></p:cTn></p:par>'

    for (const anim of group.afterPrevious) {
      effectBlocks += '<p:par>'
      effectBlocks += `<p:cTn id="${nodeId}" fill="hold"><p:stCondLst><p:cond delay="${cumulativeDelay}"/></p:stCondLst><p:childTnLst>`
      nodeId++
      effectBlocks += genEffectXml(anim, nodeId)
      nodeId += 30
      effectBlocks += '</p:childTnLst></p:cTn></p:par>'
      cumulativeDelay += (anim.duration || 1000)
    }

    effectBlocks += '</p:childTnLst></p:cTn></p:par>'
  }

  const bldShapeIds = new Set<number>()
  for (const anim of animations) {
    if (anim.type === 'in') bldShapeIds.add(anim.shapeId)
  }
  let bldLst = ''
  for (const spid of bldShapeIds) {
    bldLst += `<p:bldP spid="${spid}" grpId="0" animBg="1"/>`
  }

  return [
    '<p:timing>',
    '<p:tnLst><p:par>',
    '<p:cTn id="1" dur="indefinite" restart="never" nodeType="tmRoot">',
    '<p:childTnLst>',
    '<p:seq concurrent="1" nextAc="seek">',
    '<p:cTn id="2" dur="indefinite" nodeType="mainSeq"><p:childTnLst>',
    effectBlocks,
    '</p:childTnLst></p:cTn>',
    '<p:prevCondLst><p:cond evt="onPrev" delay="0"><p:tgtEl><p:sldTgt/></p:tgtEl></p:cond></p:prevCondLst>',
    '<p:nextCondLst><p:cond evt="onNext" delay="0"><p:tgtEl><p:sldTgt/></p:tgtEl></p:cond></p:nextCondLst>',
    '</p:seq>',
    '</p:childTnLst></p:cTn>',
    '</p:par></p:tnLst>',
    `<p:bldLst>${bldLst}</p:bldLst>`,
    '</p:timing>',
  ].join('')
}

/** 从 slide XML 中按 objectName（元素 id）查找 shape spid */
function parseShapeIdMap(slideXml: string): Map<string, number> {
  const map = new Map<string, number>()
  const re = /<p:cNvPr\b([^>]*?)(?:\/>|><\/p:cNvPr>)/g
  let match: RegExpExecArray | null
  while ((match = re.exec(slideXml)) !== null) {
    const attrs = match[1]
    const idMatch = attrs.match(/\bid="(\d+)"/)
    const nameMatch = attrs.match(/\bname="([^"]*)"/)
    if (idMatch && nameMatch && nameMatch[1]) {
      map.set(nameMatch[1], parseInt(idMatch[1], 10))
    }
  }
  return map
}

function injectTimingIntoSlideXml(slideXml: string, timingXml: string): string {
  const withoutTiming = slideXml.replace(/<p:timing[\s\S]*?<\/p:timing>/, '')
  return withoutTiming.replace('</p:sld>', `${timingXml}</p:sld>`)
}

/**
 * 向已生成的 PPTX blob 注入幻灯片元素动画。
 * 依赖导出时为 pptxgen 元素设置的 objectName（= 元素 id）。
 */
export async function injectPptxAnimations(blob: Blob, slides: Slide[]): Promise<Blob> {
  const slidesWithAnimations = slides.filter(slide => slide.animations?.length)
  if (!slidesWithAnimations.length) return blob

  const zip = await JSZip.loadAsync(blob)
  const slideFiles = Object.keys(zip.files)
    .filter(name => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => {
      const ai = parseInt(a.match(/slide(\d+)/)?.[1] || '0', 10)
      const bi = parseInt(b.match(/slide(\d+)/)?.[1] || '0', 10)
      return ai - bi
    })

  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i]
    const animations = slide.animations
    if (!animations?.length) continue

    const slidePath = slideFiles[i]
    if (!slidePath) continue

    const slideXml = await zip.file(slidePath)?.async('string')
    if (!slideXml) continue

    const shapeIdMap = parseShapeIdMap(slideXml)
    const resolved: ResolvedAnimation[] = []

    for (const anim of animations) {
      const shapeId = shapeIdMap.get(anim.elId)
      if (!shapeId) {
        console.warn(`导出 PPTX：未找到动画目标元素 shape id，elId=${anim.elId}`)
        continue
      }
      resolved.push({
        elId: anim.elId,
        effect: anim.effect,
        type: anim.type,
        duration: anim.duration,
        trigger: anim.trigger,
        shapeId,
      })
    }

    if (!resolved.length) continue

    const timingXml = buildTimingXml(resolved)
    zip.file(slidePath, injectTimingIntoSlideXml(slideXml, timingXml))
  }

  return zip.generateAsync({ type: 'blob' })
}
